import React, { useState, useEffect, useRef } from 'react';
import { 
  Check, ArrowRight, Smartphone, Monitor, Target, Sparkles,
  Play, ChevronRight, Users, FileText, Calendar, Shield, 
  TrendingUp, Bell, Clock, Activity, BarChart3, Zap,
  UserCheck, AlertCircle, CheckCircle2, Settings, Search,
  PieChart, ArrowUpRight, RefreshCw, Layers
} from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

// Animated Bar Chart Component
const AnimatedBarChart = ({ data, activeIndex }) => {
  return (
    <div className="flex items-end justify-between h-full gap-1.5 px-2">
      {data.map((bar, i) => (
        <div key={i} className="flex flex-col items-center flex-1">
          <div 
            className={`w-full rounded-t transition-all duration-700 ease-out ${
              i === activeIndex ? 'bg-care-teal' : 'bg-gradient-to-t from-tech-blue/80 to-tech-blue/40'
            }`}
            style={{ 
              height: `${bar.value}%`,
              transitionDelay: `${i * 50}ms`
            }}
          />
          <span className="text-[8px] text-white/40 mt-1">{bar.label}</span>
        </div>
      ))}
    </div>
  );
};

// Animated Line Chart Component
const AnimatedLineChart = ({ isActive }) => {
  const points = [20, 35, 25, 45, 40, 60, 55, 75, 65, 85];
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * 28} ${100 - p}`).join(' ');
  
  return (
    <svg className="w-full h-full" viewBox="0 0 252 100" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00B4A6" />
          <stop offset="100%" stopColor="#1a6cdb" />
        </linearGradient>
        <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#00B4A6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#00B4A6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path 
        d={`${pathD} L 252 100 L 0 100 Z`}
        fill="url(#areaGradient)"
        className={`transition-opacity duration-1000 ${isActive ? 'opacity-100' : 'opacity-0'}`}
      />
      <path 
        d={pathD}
        fill="none"
        stroke="url(#lineGradient)"
        strokeWidth="2.5"
        strokeLinecap="round"
        className={`transition-all duration-1000 ${isActive ? 'opacity-100' : 'opacity-50'}`}
        style={{
          strokeDasharray: 500,
          strokeDashoffset: isActive ? 0 : 500,
          transition: 'stroke-dashoffset 2s ease-out'
        }}
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={i * 28}
          cy={100 - p}
          r="4"
          fill="#00B4A6"
          className={`transition-all duration-300 ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}
          style={{ transitionDelay: `${i * 100 + 500}ms` }}
        />
      ))}
    </svg>
  );
};

// Animated Donut Chart Component
const AnimatedDonutChart = ({ segments, isActive }) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  let currentAngle = -90;
  
  return (
    <svg className="w-full h-full" viewBox="0 0 100 100">
      {segments.map((segment, i) => {
        const angle = (segment.value / total) * 360;
        const startAngle = currentAngle;
        currentAngle += angle;
        
        const x1 = 50 + 35 * Math.cos((startAngle * Math.PI) / 180);
        const y1 = 50 + 35 * Math.sin((startAngle * Math.PI) / 180);
        const x2 = 50 + 35 * Math.cos(((startAngle + angle) * Math.PI) / 180);
        const y2 = 50 + 35 * Math.sin(((startAngle + angle) * Math.PI) / 180);
        const largeArc = angle > 180 ? 1 : 0;
        
        return (
          <path
            key={i}
            d={`M 50 50 L ${x1} ${y1} A 35 35 0 ${largeArc} 1 ${x2} ${y2} Z`}
            fill={segment.color}
            className={`transition-all duration-700 ${isActive ? 'opacity-100' : 'opacity-30'}`}
            style={{ 
              transform: isActive ? 'scale(1)' : 'scale(0.8)',
              transformOrigin: 'center',
              transitionDelay: `${i * 150}ms`
            }}
          />
        );
      })}
      <circle cx="50" cy="50" r="22" fill="#0a1628" />
      <text x="50" y="48" textAnchor="middle" className="fill-white text-[10px] font-bold">
        {isActive ? '98%' : '--'}
      </text>
      <text x="50" y="58" textAnchor="middle" className="fill-white/60 text-[5px]">
        Compliance
      </text>
    </svg>
  );
};

// Workflow Animation Component
const WorkflowAnimation = ({ isActive, step }) => {
  const workflows = [
    { icon: UserCheck, label: 'Participant Added', color: 'care-teal' },
    { icon: FileText, label: 'Care Plan Created', color: 'tech-blue' },
    { icon: Calendar, label: 'Shift Scheduled', color: 'heritage-orange' },
    { icon: CheckCircle2, label: 'Service Delivered', color: 'green-500' }
  ];
  
  return (
    <div className="flex items-center justify-between px-2">
      {workflows.map((item, i) => {
        const Icon = item.icon;
        const isActiveStep = i <= step;
        const isCurrentStep = i === step;
        
        return (
          <React.Fragment key={i}>
            <div className={`flex flex-col items-center transition-all duration-500 ${
              isActiveStep ? 'opacity-100' : 'opacity-30'
            }`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                isCurrentStep 
                  ? `bg-${item.color} scale-110 shadow-lg` 
                  : isActiveStep 
                    ? `bg-${item.color}/30 border-2 border-${item.color}` 
                    : 'bg-white/10'
              }`}>
                <Icon size={18} className={isCurrentStep ? 'text-white' : `text-${item.color}`} />
              </div>
              <span className={`text-[8px] mt-2 text-center max-w-[60px] transition-colors ${
                isActiveStep ? 'text-white/80' : 'text-white/40'
              }`}>
                {item.label}
              </span>
            </div>
            {i < workflows.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 transition-all duration-500 ${
                i < step ? 'bg-care-teal' : 'bg-white/10'
              }`}>
                <div 
                  className="h-full bg-care-teal transition-all duration-700"
                  style={{ width: i < step ? '100%' : i === step ? '50%' : '0%' }}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Floating Notification Component
const FloatingNotification = ({ isVisible, delay, type }) => {
  const notifications = {
    success: { icon: CheckCircle2, text: 'Shift completed', color: 'bg-green-500', time: 'Just now' },
    alert: { icon: Bell, text: 'New participant assigned', color: 'bg-tech-blue', time: '2m ago' },
    update: { icon: RefreshCw, text: 'Care plan updated', color: 'bg-heritage-orange', time: '5m ago' }
  };
  
  const notif = notifications[type];
  const Icon = notif.icon;
  
  return (
    <div 
      className={`absolute bg-slate-800/95 backdrop-blur-sm border border-white/10 rounded-lg p-2.5 shadow-xl transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-full ${notif.color} flex items-center justify-center`}>
          <Icon size={14} className="text-white" />
        </div>
        <div>
          <p className="text-white text-[10px] font-medium">{notif.text}</p>
          <p className="text-white/40 text-[8px]">{notif.time}</p>
        </div>
      </div>
    </div>
  );
};

const YourPlatformSection = ({ openBooking }) => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeView, setActiveView] = useState(0);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [chartBarIndex, setChartBarIndex] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  const features = [
    'Custom participant management for your workflow',
    'Staff credential tracking with automatic alerts',
    'NDIS-compliant incident reporting & escalation',
    'Automated service agreements using your templates',
    'AI-powered compliance dashboard',
    'Mobile app for support workers in the field',
    'Integration with your existing systems'
  ];

  const dashboardViews = ['overview', 'analytics', 'workflow', 'compliance'];
  
  const barChartData = [
    { label: 'Mon', value: 65 },
    { label: 'Tue', value: 80 },
    { label: 'Wed', value: 55 },
    { label: 'Thu', value: 90 },
    { label: 'Fri', value: 75 },
    { label: 'Sat', value: 45 },
    { label: 'Sun', value: 30 }
  ];

  const donutSegments = [
    { value: 65, color: '#00B4A6' },
    { value: 20, color: '#1a6cdb' },
    { value: 10, color: '#FF6B35' },
    { value: 5, color: '#6366f1' }
  ];

  // Intersection Observer for viewport detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-cycle through features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  // Auto-cycle through dashboard views
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveView((prev) => (prev + 1) % dashboardViews.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Workflow animation
  useEffect(() => {
    const interval = setInterval(() => {
      setWorkflowStep((prev) => (prev + 1) % 5);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Chart bar highlight
  useEffect(() => {
    const interval = setInterval(() => {
      setChartBarIndex((prev) => (prev + 1) % barChartData.length);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Notification animation
  useEffect(() => {
    const interval = setInterval(() => {
      setShowNotifications(true);
      setTimeout(() => setShowNotifications(false), 3000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-surface relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-care-teal/10 to-tech-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }}></div>
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-gradient-to-br from-tech-blue/10 to-care-teal/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s', animationDelay: '2s' }}></div>
        
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(#0a2540 1px, transparent 1px), linear-gradient(90deg, #0a2540 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
        
        {/* Floating Particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-care-teal/30 rounded-full animate-float"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + i * 0.5}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-care-teal/10 rounded-full border border-care-teal/20 mb-6">
              <Check className="text-care-teal mr-2" size={18} />
              <span className="text-sm font-medium text-text-primary">The GetCare Solution</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-primary-navy mb-6 leading-tight">
              <span className="block">Your NDIS Business.</span>
              <span className="block">Your Workflow.</span>
              <span className="block bg-gradient-to-r from-tech-blue to-care-teal bg-clip-text text-transparent">
                Your Custom Platform.
              </span>
            </h2>
            
            <p className="text-xl text-text-secondary mb-8">
              GetCare is not a template. We build a <strong className="text-text-primary">custom AI-powered platform</strong> tailored specifically to how your NDIS organisation operates.
            </p>

            {/* Animated Feature List */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-500 cursor-pointer ${
                    activeFeature === index 
                      ? 'bg-care-teal/10 border border-care-teal/30 scale-[1.02]' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    activeFeature === index 
                      ? 'bg-care-teal scale-110' 
                      : 'bg-care-teal/20'
                  }`}>
                    <Check className={`${activeFeature === index ? 'text-white' : 'text-care-teal'}`} size={14} />
                  </div>
                  <span className={`font-medium transition-colors ${
                    activeFeature === index ? 'text-text-primary' : 'text-text-secondary'
                  }`}>
                    {feature}
                  </span>
                  {activeFeature === index && (
                    <ChevronRight className="text-care-teal ml-auto animate-pulse" size={18} />
                  )}
                </div>
              ))}
            </div>

            <Link to="/solutions">
              <Button className="bg-tech-blue hover:bg-[#1558C9] text-white px-8 py-6 text-lg group hover:shadow-lg hover:shadow-tech-blue/30 transition-all">
                Explore Solutions
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>

            {/* Mobile-Only Simplified Mockup */}
            <div className="mt-12 lg:hidden">
              <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-3 shadow-2xl border border-slate-700 max-w-sm mx-auto">
                {/* Browser Header */}
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-t-lg border-b border-slate-700">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 mx-2">
                    <div className="bg-slate-700 rounded px-2 py-0.5 text-[10px] text-slate-400 text-center">
                      app.getcare360.com.au
                    </div>
                  </div>
                </div>
                
                {/* Screen Content */}
                <div className="bg-gradient-to-br from-[#0a1628] to-[#0f1f35] rounded-b-lg p-4">
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {[
                      { label: 'Participants', value: '247', icon: Users, color: 'care-teal' },
                      { label: 'Compliance', value: '98%', icon: Shield, color: 'green-500' }
                    ].map((stat, i) => {
                      const Icon = stat.icon;
                      return (
                        <div 
                          key={i} 
                          className="bg-white/10 rounded-lg p-3 border border-white/10"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Icon size={14} className={`text-${stat.color}`} />
                          </div>
                          <p className="text-white text-lg font-bold">{stat.value}</p>
                          <p className="text-white/40 text-[10px]">{stat.label}</p>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Mini Chart */}
                  <div className="bg-white/5 rounded-lg p-3 h-20">
                    <div className="flex items-end justify-between h-full gap-1">
                      {[40, 65, 45, 80, 55, 70, 85].map((height, i) => (
                        <div 
                          key={i} 
                          className="flex-1 bg-gradient-to-t from-care-teal to-tech-blue rounded-t transition-all duration-500"
                          style={{ height: `${height}%` }}
                        ></div>
                      ))}
                    </div>
                  </div>

                  {/* AI Badge */}
                  <div className="mt-3 flex justify-center">
                    <div className="bg-care-teal/20 text-care-teal text-xs px-3 py-1 rounded-full flex items-center gap-1">
                      <Sparkles size={12} />
                      AI-Powered Dashboard
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Advanced Interactive Device Mockups */}
          <div className="relative hidden lg:block">
            {/* Main Device Container */}
            <div className="relative">
              {/* Desktop/Laptop Mockup */}
              <div className="relative">
                {/* Laptop Base */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[90%] h-4 bg-gradient-to-b from-slate-600 to-slate-700 rounded-b-xl"></div>
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-[70%] h-2 bg-slate-700 rounded-b-lg"></div>
                
                {/* Screen Frame */}
                <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-2 shadow-2xl border border-slate-700">
                  {/* Browser Header */}
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 rounded-t-lg border-b border-slate-700">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-400 transition-colors cursor-pointer"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500 hover:bg-yellow-400 transition-colors cursor-pointer"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-400 transition-colors cursor-pointer"></div>
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-slate-700 rounded-lg px-4 py-1.5 text-xs text-slate-400 flex items-center gap-2 max-w-[280px] mx-auto">
                        <Shield size={12} className="text-green-500" />
                        <span>app.getcare360.com.au</span>
                        <div className="ml-auto flex items-center gap-1">
                          <RefreshCw size={10} className="text-slate-500" />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 items-center">
                      <Search size={14} className="text-slate-500" />
                      <Settings size={14} className="text-slate-500" />
                      <div className="w-6 h-6 rounded-full bg-care-teal/30 flex items-center justify-center">
                        <span className="text-[10px] text-care-teal font-bold">JD</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* App Content */}
                  <div className="bg-gradient-to-br from-[#0a1628] to-[#0f1f35] rounded-b-lg aspect-[16/10] relative overflow-hidden">
                    {/* Sidebar */}
                    <div className="absolute left-0 top-0 bottom-0 w-14 bg-slate-900/80 border-r border-white/5 flex flex-col items-center py-4 gap-3">
                      <div className="w-8 h-8 rounded-lg bg-care-teal flex items-center justify-center">
                        <Layers size={16} className="text-white" />
                      </div>
                      {[BarChart3, Users, Calendar, FileText, Bell].map((Icon, i) => (
                        <div 
                          key={i}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                            activeView === i ? 'bg-white/10 text-care-teal' : 'text-white/40 hover:text-white/60'
                          }`}
                        >
                          <Icon size={16} />
                        </div>
                      ))}
                    </div>

                    {/* Main Dashboard Area */}
                    <div className="ml-14 p-4 h-full flex flex-col">
                      {/* Top Bar */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-white font-semibold text-sm">Dashboard Overview</h3>
                          <p className="text-white/40 text-[10px]">Welcome back, Jane</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="bg-care-teal/20 text-care-teal text-[10px] px-2 py-1 rounded-full flex items-center gap-1">
                            <Sparkles size={10} />
                            AI Insights Active
                          </div>
                        </div>
                      </div>

                      {/* View-based Content */}
                      <div className="flex-1 relative">
                        {/* Overview View */}
                        <div className={`absolute inset-0 transition-all duration-500 ${activeView === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                          {/* Stats Row */}
                          <div className="grid grid-cols-4 gap-2 mb-3">
                            {[
                              { label: 'Participants', value: '247', change: '+12', icon: Users, color: 'care-teal' },
                              { label: 'Active Staff', value: '89', change: '+5', icon: UserCheck, color: 'tech-blue' },
                              { label: 'Shifts Today', value: '32', change: '+8', icon: Calendar, color: 'heritage-orange' },
                              { label: 'Compliance', value: '98%', change: '+2%', icon: Shield, color: 'green-500' }
                            ].map((stat, i) => {
                              const Icon = stat.icon;
                              return (
                                <div 
                                  key={i} 
                                  className={`bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10 transition-all duration-500 hover:bg-white/10 ${
                                    isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                                  }`}
                                  style={{ transitionDelay: `${i * 100}ms` }}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <Icon size={14} className={`text-${stat.color}`} />
                                    <span className={`text-[9px] text-${stat.color} bg-${stat.color}/20 px-1.5 py-0.5 rounded`}>
                                      {stat.change}
                                    </span>
                                  </div>
                                  <p className="text-white text-lg font-bold">{stat.value}</p>
                                  <p className="text-white/40 text-[9px]">{stat.label}</p>
                                </div>
                              );
                            })}
                          </div>

                          {/* Charts Row */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-white/60 text-[10px] font-medium">Weekly Services</p>
                                <TrendingUp size={12} className="text-care-teal" />
                              </div>
                              <div className="h-24">
                                <AnimatedBarChart data={barChartData} activeIndex={chartBarIndex} />
                              </div>
                            </div>
                            <div className="bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-white/60 text-[10px] font-medium">Growth Trend</p>
                                <ArrowUpRight size={12} className="text-green-500" />
                              </div>
                              <div className="h-24">
                                <AnimatedLineChart isActive={isInView && activeView === 0} />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Analytics View */}
                        <div className={`absolute inset-0 transition-all duration-500 ${activeView === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                          <div className="grid grid-cols-3 gap-3 h-full">
                            <div className="col-span-2 bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10">
                              <p className="text-white/60 text-[10px] font-medium mb-2">Service Delivery Analytics</p>
                              <div className="h-32">
                                <AnimatedLineChart isActive={activeView === 1} />
                              </div>
                            </div>
                            <div className="bg-white/5 backdrop-blur rounded-lg p-3 border border-white/10">
                              <p className="text-white/60 text-[10px] font-medium mb-2">Distribution</p>
                              <div className="h-24 w-24 mx-auto">
                                <AnimatedDonutChart segments={donutSegments} isActive={activeView === 1} />
                              </div>
                              <div className="grid grid-cols-2 gap-1 mt-2">
                                {['SIL', 'Support', 'Coord', 'Other'].map((item, i) => (
                                  <div key={i} className="flex items-center gap-1">
                                    <div className={`w-2 h-2 rounded-full`} style={{ backgroundColor: donutSegments[i]?.color }}></div>
                                    <span className="text-white/40 text-[8px]">{item}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Workflow View */}
                        <div className={`absolute inset-0 transition-all duration-500 ${activeView === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                          <div className="bg-white/5 backdrop-blur rounded-lg p-4 border border-white/10 h-full">
                            <p className="text-white/60 text-[10px] font-medium mb-4">Service Workflow Pipeline</p>
                            <WorkflowAnimation isActive={activeView === 2} step={workflowStep} />
                            <div className="mt-6 grid grid-cols-3 gap-2">
                              {[
                                { label: 'In Progress', value: '24', color: 'tech-blue' },
                                { label: 'Completed', value: '156', color: 'green-500' },
                                { label: 'Pending', value: '8', color: 'heritage-orange' }
                              ].map((item, i) => (
                                <div key={i} className={`bg-${item.color}/10 rounded-lg p-2 text-center border border-${item.color}/20`}>
                                  <p className={`text-${item.color} text-lg font-bold`}>{item.value}</p>
                                  <p className="text-white/40 text-[8px]">{item.label}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Compliance View */}
                        <div className={`absolute inset-0 transition-all duration-500 ${activeView === 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                          <div className="bg-white/5 backdrop-blur rounded-lg p-4 border border-white/10 h-full">
                            <div className="flex items-center justify-between mb-4">
                              <p className="text-white/60 text-[10px] font-medium">Compliance Status</p>
                              <div className="flex items-center gap-1 text-green-500 text-[10px]">
                                <CheckCircle2 size={12} />
                                All Clear
                              </div>
                            </div>
                            <div className="space-y-2">
                              {[
                                { label: 'Staff Credentials', progress: 95, status: 'ok' },
                                { label: 'Care Plans', progress: 100, status: 'ok' },
                                { label: 'Incident Reports', progress: 88, status: 'warning' },
                                { label: 'Training Records', progress: 100, status: 'ok' }
                              ].map((item, i) => (
                                <div key={i} className="bg-white/5 rounded-lg p-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-white/60 text-[9px]">{item.label}</span>
                                    <span className={`text-[9px] ${item.status === 'ok' ? 'text-green-500' : 'text-yellow-500'}`}>
                                      {item.progress}%
                                    </span>
                                  </div>
                                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full transition-all duration-1000 ${
                                        item.status === 'ok' ? 'bg-green-500' : 'bg-yellow-500'
                                      }`}
                                      style={{ 
                                        width: activeView === 3 ? `${item.progress}%` : '0%',
                                        transitionDelay: `${i * 150}ms`
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* View Indicator */}
                      <div className="flex justify-center gap-1.5 mt-2">
                        {dashboardViews.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveView(i)}
                            className={`transition-all duration-300 rounded-full ${
                              activeView === i ? 'w-6 h-1.5 bg-care-teal' : 'w-1.5 h-1.5 bg-white/20 hover:bg-white/40'
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Floating Notifications */}
                    <div className="absolute top-16 right-4">
                      <FloatingNotification isVisible={showNotifications} delay={0} type="success" />
                    </div>
                    <div className="absolute top-28 right-4">
                      <FloatingNotification isVisible={showNotifications} delay={200} type="alert" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tablet Mockup - Floating Left */}
              <div className="absolute -left-16 top-1/2 -translate-y-1/2 w-40 transform hover:scale-105 transition-transform duration-300 animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="bg-slate-900 rounded-2xl p-1.5 shadow-2xl border border-slate-700">
                  <div className="bg-gradient-to-br from-[#0a1628] to-[#0f1f35] rounded-xl p-3 aspect-[3/4] relative overflow-hidden">
                    {/* Tablet Header */}
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-white/60 text-[8px]">Roster View</span>
                      <Calendar size={10} className="text-heritage-orange" />
                    </div>
                    
                    {/* Roster Items */}
                    <div className="space-y-2">
                      {['Morning Shift', 'Afternoon', 'Evening'].map((shift, i) => (
                        <div key={i} className="bg-white/10 rounded-lg p-2 border border-white/5">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white text-[8px] font-medium">{shift}</p>
                              <p className="text-white/40 text-[6px]">{6 - i} staff assigned</p>
                            </div>
                            <div className="flex -space-x-1">
                              {[...Array(3)].map((_, j) => (
                                <div key={j} className="w-4 h-4 rounded-full bg-gradient-to-br from-care-teal to-tech-blue border border-slate-800"></div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Tablet Label */}
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                      <div className="bg-white/10 backdrop-blur rounded-full px-2 py-0.5 flex items-center gap-1">
                        <Monitor size={8} className="text-white/60" />
                        <span className="text-white/60 text-[7px]">Tablet</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Mockup - Floating Right */}
              <div className="absolute -right-8 -bottom-8 w-44 transform hover:scale-105 transition-transform duration-300 animate-float" style={{ animationDelay: '1s' }}>
                <div className="bg-slate-900 rounded-[2rem] p-2 shadow-2xl border-4 border-slate-700">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-5 bg-slate-900 rounded-b-xl flex items-center justify-center">
                    <div className="w-10 h-2 bg-slate-800 rounded-full"></div>
                  </div>
                  
                  {/* Screen */}
                  <div className="bg-gradient-to-br from-[#0a1628] to-[#0f1f35] rounded-[1.5rem] p-3 aspect-[9/16] relative overflow-hidden">
                    {/* Status Bar */}
                    <div className="flex justify-between items-center mb-3 mt-2">
                      <span className="text-white/60 text-[9px]">9:41</span>
                      <div className="flex gap-1 items-center">
                        <Activity size={10} className="text-care-teal" />
                        <div className="w-5 h-2.5 bg-care-teal rounded-sm flex items-center justify-end pr-0.5">
                          <div className="w-0.5 h-1 bg-white/80 rounded-full"></div>
                        </div>
                      </div>
                    </div>

                    {/* App Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-care-teal/20 flex items-center justify-center">
                        <Layers size={12} className="text-care-teal" />
                      </div>
                      <div>
                        <p className="text-white text-[9px] font-medium">GetCare 360</p>
                        <p className="text-white/40 text-[7px]">Support Worker</p>
                      </div>
                    </div>

                    {/* Mobile Content */}
                    <div className="space-y-2">
                      <div className="bg-care-teal/20 rounded-lg p-2.5 border border-care-teal/30">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-care-teal flex items-center justify-center">
                            <Check className="text-white" size={12} />
                          </div>
                          <div>
                            <span className="text-white text-[9px] font-medium">Shift Started</span>
                            <p className="text-white/60 text-[7px]">Sarah M. - Morning Care</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white/10 rounded-lg p-2.5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white/60 text-[8px]">Today's Tasks</span>
                          <span className="text-care-teal text-[8px]">3/5</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full w-3/5 bg-gradient-to-r from-care-teal to-tech-blue rounded-full"></div>
                        </div>
                      </div>

                      <div className="bg-white/10 rounded-lg p-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-tech-blue/20 flex items-center justify-center">
                            <Users size={14} className="text-tech-blue" />
                          </div>
                          <div className="flex-1">
                            <p className="text-white text-[8px]">Next: James Cooper</p>
                            <p className="text-white/40 text-[7px]">2:00 PM - Therapy Session</p>
                          </div>
                          <ChevronRight size={12} className="text-white/30" />
                        </div>
                      </div>
                    </div>

                    {/* Mobile Navigation */}
                    <div className="absolute bottom-3 left-3 right-3 bg-white/5 backdrop-blur rounded-full p-1.5 flex justify-around">
                      {[{ icon: Layers, active: true }, { icon: Calendar }, { icon: Users }, { icon: Bell }].map((item, i) => {
                        const Icon = item.icon;
                        return (
                          <div 
                            key={i}
                            className={`w-7 h-7 rounded-full flex items-center justify-center ${
                              item.active ? 'bg-care-teal' : 'bg-transparent'
                            }`}
                          >
                            <Icon size={12} className={item.active ? 'text-white' : 'text-white/40'} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Badge - Floating */}
              <div className="absolute -bottom-6 left-8 bg-white p-4 rounded-xl shadow-xl border border-border-line animate-float" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-care-teal/20 to-tech-blue/20 flex items-center justify-center">
                    <Zap className="text-care-teal" size={24} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold bg-gradient-to-r from-care-teal to-tech-blue bg-clip-text text-transparent">60%</div>
                    <div className="text-sm text-text-secondary">Less Admin Time</div>
                  </div>
                </div>
              </div>

              {/* AI Badge - Top Right */}
              <div className="absolute -top-4 right-12 bg-gradient-to-r from-care-teal to-tech-blue p-3 rounded-xl shadow-xl animate-float" style={{ animationDelay: '2s' }}>
                <div className="flex items-center gap-2">
                  <Sparkles className="text-white" size={18} />
                  <span className="text-white text-sm font-medium">AI-Powered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default YourPlatformSection;
